export class SlideInElementFramePositioner {
	constructor(initiator) {
		this.initiator = initiator;
	}

	/**
	 * Take care of triggering all the necessary steps to position the slide-in
	 * element that was passed along on the page.
	 *
	 * @param {HTMLElement} element
	 * @return void
	 */

	positionElement(element) {
		this.element = element;

		// Enrich the list of specific contextual elements with universal contextual elements.
		const contextualElements = this.getContextualElements();

		// Set the position of the slide-in element.
		this.updatePosition(contextualElements);

		// Prepare for any user interactions that might have an impact on the slide-in element.
		this.prepareForContextualChanges(contextualElements);
		this.prepareCrossClose();

		// Reveal the element.
		this.showElement();
	}

	/**
	 * Combine into one list the specific contextual elements that were passed along,
	 * with any universal contextual elements that the universal informer provides.
	 *
	 * @returns {Array}
	 */

	getContextualElements() {
		const contextualElements = this.initiator.contextualInformer.getContextualElements();
		const page = this.initiator.contextualInformer.getCurrentPage();

		return contextualElements.filter((element) => !element.pages.length || element.pages.includes(page));
	}

	/**
	 * Retrieve the ideal position of the slide-in element based on contextual
	 * elements and position the slide-in element accordingly.
	 *
	 * @param {array} contextualElements
	 * @return void
	 */

	updatePosition(contextualElements) {
		const position = this.getElementPosition(contextualElements);

		const frame = this.element.parentNode;
		let spacing = 12;

		if (window.innerWidth > 480) {
			spacing = 24;
		}

		frame.style.bottom = position.vertical ? `${spacing + position.vertical}px` : null;
		frame.style.right = position.horizontal ? `${spacing + position.horizontal}px` : null;
	}

	/**
	 * Calculate the ideal position of the slide-in element by taking any contextual
	 * elements in mind that the slide-in element shouldn't overlap with.
	 *
	 * @param {array} contextualElements
	 * @returns {{horizontal: number, vertical: number}}
	 */

	getElementPosition(contextualElements) {
		let position = {
			horizontal: 0,
			vertical: 0
		};

		// Check the contextual elements one by one.
		contextualElements.forEach((info) => {
			if (!info.element.isActive()) {
				// Contextual element is not active, so nothing to take into account.
				return;
			}

			const element = document.querySelector(info.element.selector);
			let offset = info.element.extraOffset ? info.element.extraOffset : 0;

			// Pick the right offset to set, based on the direction in which the slide-in element should re-position.
			if (info.motion.direction === 'horizontal') {
				offset += window.innerWidth - element.offsetLeft;
			} else {
				offset += window.innerHeight - element.offsetTop;
			}

			// Only overwrite the position if a bigger offset wasn't already set.
			if (offset > position[info.motion.direction]) {
				position[info.motion.direction] = offset;
			}
		});

		return position;
	}

	/**
	 * Set up event listeners for the contextual elements that have to be considered,
	 * so that, when any of these elements (dis)appear, the ideal position of the
	 * slide-in element can be re-calculated.
	 *
	 * @param {array} contextualElements
	 * @return void
	 */

	prepareForContextualChanges(contextualElements) {
		contextualElements.forEach((info) => {
			// Go through all the pre-defined listeners of each contextual element.
			info.listeners.forEach((listener) => {
				// Prepare the right element to attach the listener to.
				var element;
				switch (listener.selector) {
					case 'window':
						element = window;
						break;

					case 'document':
						element = document;
						break;

					default:
						element = document.querySelector(listener.selector);
						break;
				}

				if (!element) {
					console.warn(
						'Positioner.prepareForContextualChanges: element to attach listener to not found.',
						listener.selector
					);
					return;
				}

				// Set up the event listener that should trigger the slide-in element position to be updated.
				element.addEventListener(listener.action, () => {
					setTimeout(() => {
						this.updatePosition(contextualElements);
					}, info.motion.timing);
				});
			});
		});
	}

	/**
	 * Set up the close interaction when the 'x' in the top-right corner is
	 * clicked.
	 *
	 * @return void
	 */

	prepareCrossClose() {
		const cross = this.element.querySelector('.js-slideInElementCross');
		if (!cross) {
			console.warn("Positioner.prepareCrossClose: '.js-slideInElementCross' not found.");
			return;
		}

		cross.addEventListener('click', () => {
			this.initiator.tracker.fireTracking(this.element.dataset.name, 'CloseClick');
			this.hideElement();
		});
	}

	/**
	 * Function that takes care of the (dis)appearance of this slide-in element and
	 * announces it by dispatching an event (so that other functionality can be
	 * triggered) and firing tracking.
	 *
	 * @param {string} action
	 * @return void
	 */

	switchElement(action) {
		this.element.classList[action === 'Shown' ? 'add' : 'remove']('SlideInElementShown');
		this.initiator.tracker.fireTracking(this.element.dataset.name, `Element${action}`);

		document.dispatchEvent(
			new CustomEvent(`SlideInElement${action}`, {
				detail: {
					name: this.element.dataset.name
				}
			})
		);
	}

	/**
	 * Function to let the slide-in element appear.
	 *
	 * @return void
	 */

	showElement() {
		this.initiator.positioner.switchElement('Shown');
	}

	/**
	 * Function to let the slide-in element disappear.
	 *
	 * @return void
	 */

	hideElement() {
		this.initiator.positioner.switchElement('Hidden');
	}
}

window.SlideInElementFramePositioner = SlideInElementFramePositioner;
